/**
 * VastuRoomPlanner - Interactive room mapping with compatibility scores
 * 
 * Allows users to map rooms to directions and calculates compatibility
 * based on lagna, planetary placements, strength, and functional nature.
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Sheet,
  Table,
  Select,
  Option,
  Chip,
  Stack,
  Button,
  Alert,
  IconButton,
  Tooltip
} from '@mui/joy';
import InfoIcon from '@mui/icons-material/Info';
import { PlanetName } from '../utils/astroEngine';
import { useIsMobile } from '../hooks/useIsMobile';
import { VastuDirection } from '../utils/vastuUtils';
// Import Vastu room utilities
import {
  calculateRoomCompatibility,
  getRoomRecommendations,
  VastuRoomType,
  ROOM_TYPES
} from '../utils/vastuRoomUtils';

interface VastuRoomPlannerProps {
  placements: Record<string, number>;
  houseLords: Record<number, PlanetName>;
  ascSign: number;
  functionalBenefics?: Set<PlanetName>;
  functionalMalefics?: Set<PlanetName>;
  yogakarakas?: Set<PlanetName>;
  exaltedPlanets?: Record<string, boolean>;
  shadbalaScores?: Partial<Record<PlanetName, number>>;
  blendedScores?: Partial<Record<PlanetName, number>>;
}

interface RoomMapping {
  direction: VastuDirection;
  roomType: VastuRoomType | null;
}

const DIRECTIONS: VastuDirection[] = [
  'North', 'Northeast', 'East', 'Southeast',
  'South', 'Southwest', 'West', 'Northwest'
];

const DIRECTION_GRID_POS: Record<VastuDirection, { row: number; col: number }> = {
  'Northwest': { row: 0, col: 0 },
  'North': { row: 0, col: 1 },
  'Northeast': { row: 0, col: 2 },
  'West': { row: 1, col: 0 },
  'Center': { row: 1, col: 1 },
  'East': { row: 1, col: 2 },
  'Southwest': { row: 2, col: 0 },
  'South': { row: 2, col: 1 },
  'Southeast': { row: 2, col: 2 },
};

export default function VastuRoomPlanner(props: VastuRoomPlannerProps) {
  const {
    placements,
    houseLords,
    ascSign,
    functionalBenefics,
    functionalMalefics,
    yogakarakas,
    exaltedPlanets,
    shadbalaScores,
    blendedScores,
  } = props;

  const { isMobile } = useIsMobile();

  // Persist room mappings per ascendant sign so selections survive tab switches
  const storageKey = `vastu_rooms_${ascSign}`;
  const defaultRoomMappings: Record<VastuDirection, VastuRoomType[]> = {
    'North': [], 'Northeast': [], 'East': [], 'Southeast': [],
    'South': [], 'Southwest': [], 'West': [], 'Northwest': [], 'Center': [],
  };
  const [roomMappings, setRoomMappings] = useState<Record<VastuDirection, VastuRoomType[]>>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return { ...defaultRoomMappings, ...JSON.parse(saved) };
    } catch { /* ignore storage errors */ }
    return defaultRoomMappings;
  });

  // Persist to localStorage on every change
  React.useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(roomMappings)); } catch { /* ignore */ }
  }, [roomMappings, storageKey]);

  // Full compatibility matrix: hidden on mobile by default, toggleable on desktop
  const [showFullMatrix, setShowFullMatrix] = useState(!isMobile);

  // Calculate compatibility scores for all directions and room types
  const compatibilityMatrix = useMemo(() => {
    const matrix: Record<VastuDirection, Record<VastuRoomType, {
      score: number;
      interpretation: string;
      energyAnalysis: string;
      recommendation: 'preferred' | 'suitable' | 'neutral' | 'avoid' | 'strictly_avoid';
    }>> = {} as any;

    for (const direction of [...DIRECTIONS, 'Center'] as VastuDirection[]) {
      matrix[direction] = {} as any;
      
      for (const roomType of ROOM_TYPES) {
        const result = calculateRoomCompatibility(
          direction,
          roomType,
          {
            placements,
            houseLords,
            ascSign,
            functionalBenefics,
            functionalMalefics,
            yogakarakas,
            exaltedPlanets,
            shadbalaScores,
            blendedScores,
          }
        );
        
        matrix[direction][roomType] = result;
      }
    }

    return matrix;
  }, [placements, houseLords, ascSign, functionalBenefics, functionalMalefics, yogakarakas, exaltedPlanets, shadbalaScores, blendedScores]);

  // Get recommendations for each room type
  const roomRecommendations = useMemo(() => {
    return getRoomRecommendations(compatibilityMatrix);
  }, [compatibilityMatrix]);

  const handleRoomSelect = (direction: VastuDirection, roomTypes: VastuRoomType[]) => {
    setRoomMappings(prev => ({
      ...prev,
      [direction]: roomTypes
    }));
  };

  const getScoreColor = (score: number): 'success' | 'primary' | 'warning' | 'danger' => {
    if (score >= 8) return 'success';
    if (score >= 6) return 'primary';
    if (score >= 4) return 'warning';
    return 'danger';
  };

  const getRecommendationColor = (rec: string): 'success' | 'primary' | 'neutral' | 'warning' | 'danger' => {
    if (rec === 'preferred') return 'success';
    if (rec === 'suitable') return 'primary';
    if (rec === 'neutral') return 'neutral';
    if (rec === 'avoid') return 'warning';
    return 'danger';
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography level="title-lg">Room Planning & Compatibility Analysis</Typography>
        <Tooltip title="Map your rooms to directions and get personalized compatibility scores based on your birth chart">
          <IconButton size="sm" variant="plain">
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Alert color="primary" variant="soft" sx={{ mb: 3 }}>
        <Typography level="body-sm">
          <strong>How to use:</strong> Select a room type for each direction to see its compatibility score (0-10).
          Green = Highly Compatible, Red = Avoid. Scores are personalized based on your ascendant,
          planetary placements, and functional benefics/malefics.
        </Typography>
      </Alert>

      {/* 3x3 Grid for Room Mapping */}
      <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 3 }}>
        <Typography level="title-sm" sx={{ mb: 2 }}>Map Your Rooms (Reference: Center of House)</Typography>
        
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          {Array.from({ length: 9 }).map((_, idx) => {
            const row = Math.floor(idx / 3);
            const col = idx % 3;
            
            // Find direction for this grid position
            const direction = Object.entries(DIRECTION_GRID_POS).find(
              ([_, pos]) => pos.row === row && pos.col === col
            )?.[0] as VastuDirection | undefined;

            if (!direction) return null;

            const selectedRooms = roomMappings[direction] || [];

            return (
              <Sheet
                key={direction}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 'sm',
                  textAlign: 'center',
                  bgcolor: direction === 'Center' ? 'neutral.softBg' : 'background.surface',
                  minHeight: 140,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                  {direction}
                </Typography>
                
                <Select
                  size="sm"
                  multiple
                  placeholder="Select rooms"
                  value={selectedRooms}
                  onChange={(_, value) => handleRoomSelect(direction, value as VastuRoomType[])}
                  disabled={direction === 'Center'}
                  sx={{ mb: 0.5, fontSize: '0.75rem' }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selected.map((option) => (
                        <Chip key={option.value} size="sm" variant="soft" sx={{ fontSize: '0.65rem' }}>
                          {option.label}
                        </Chip>
                      ))}
                    </Box>
                  )}
                >
                  {ROOM_TYPES.map((room: VastuRoomType) => (
                    <Option key={room} value={room}>
                      {room}
                    </Option>
                  ))}
                </Select>

                {selectedRooms.length > 0 && (
                  <Stack spacing={0.5} sx={{ mt: 'auto' }}>
                    {selectedRooms.map((room) => {
                      const score = compatibilityMatrix[direction]?.[room]?.score;
                      return (
                        <Chip
                          key={room}
                          size="sm"
                          color={getScoreColor(score || 0)}
                          variant="solid"
                          sx={{ fontSize: '0.65rem' }}
                        >
                          {room}: {score?.toFixed(1) || 'N/A'}/10
                        </Chip>
                      );
                    })}
                  </Stack>
                )}
              </Sheet>
            );
          })}
        </Box>
      </Sheet>

      {/* Compatibility Analysis — toggle between full matrix and top-picks */}
      <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto', mb: 3 }}>
        <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography level="title-sm">
            {showFullMatrix ? 'Complete Compatibility Matrix (All Rooms × All Directions)' : 'Top Direction per Room'}
          </Typography>
          {!isMobile && (
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => setShowFullMatrix(v => !v)}
            >
              {showFullMatrix ? '↑ Collapse to Top Picks' : '↓ Show Full Matrix (13 × 8)'}
            </Button>
          )}
        </Box>

        {showFullMatrix ? (
          <Table
            stickyHeader
            size="sm"
            sx={{
              '& th': {
                bgcolor: 'background.level1',
                fontWeight: 600,
                fontSize: '0.75rem',
              },
              '& td': {
                py: 0.75,
                px: 1,
                fontSize: '0.75rem',
              },
            }}
          >
            <thead>
              <tr>
                <th style={{ width: '15%' }}>Room Type</th>
                {DIRECTIONS.map(dir => (
                  <th key={dir} style={{ width: '10%', textAlign: 'center' }}>{dir}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROOM_TYPES.map((roomType: VastuRoomType) => {
                const selectedDirections = DIRECTIONS.filter(dir =>
                  roomMappings[dir]?.includes(roomType)
                );
                const isRowHighlighted = selectedDirections.length > 0;
                return (
                  <tr key={roomType} style={{ backgroundColor: isRowHighlighted ? 'rgba(25, 118, 210, 0.08)' : undefined }}>
                    <td>
                      <Typography level="body-xs" fontWeight={isRowHighlighted ? 'xl' : 'lg'}>
                        {roomType}
                        {isRowHighlighted && (
                          <Chip size="sm" variant="soft" color="primary" sx={{ ml: 0.5, fontSize: '0.6rem' }}>
                            Selected
                          </Chip>
                        )}
                      </Typography>
                    </td>
                    {DIRECTIONS.map((direction: VastuDirection) => {
                      const compat = compatibilityMatrix[direction]?.[roomType];
                      if (!compat) return <td key={direction}>-</td>;
                      const isSelected = roomMappings[direction]?.includes(roomType);
                      return (
                        <td key={direction} style={{ textAlign: 'center', backgroundColor: isSelected ? 'rgba(46, 125, 50, 0.15)' : undefined }}>
                          <Tooltip
                            title={
                              <Box>
                                <Typography level="body-xs" fontWeight="bold">{compat.interpretation}</Typography>
                                <Typography level="body-xs" sx={{ mt: 0.5 }}>{compat.energyAnalysis}</Typography>
                              </Box>
                            }
                            variant="outlined"
                          >
                            <Chip
                              size="sm"
                              color={getScoreColor(compat.score)}
                              variant={isSelected ? 'solid' : 'soft'}
                              sx={{ fontSize: '0.7rem', cursor: 'help', fontWeight: isSelected ? 'bold' : 'normal' }}
                            >
                              {compat.score.toFixed(1)}
                            </Chip>
                          </Tooltip>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : (
          /* Compact top-picks view — one best + one worst per room */
          <Table
            size="sm"
            sx={{
              '& th': { bgcolor: 'background.level1', fontWeight: 600, fontSize: '0.75rem' },
              '& td': { py: 0.75, px: 1, fontSize: '0.75rem' },
            }}
          >
            <thead>
              <tr>
                <th style={{ width: '35%' }}>Room Type</th>
                <th style={{ width: '35%' }}>Best Direction</th>
                <th style={{ width: '30%' }}>Avoid</th>
              </tr>
            </thead>
            <tbody>
              {ROOM_TYPES.map((roomType: VastuRoomType) => {
                const rec = roomRecommendations[roomType];
                const bestDir = rec?.preferred[0] || rec?.suitable[0];
                const worstDir = rec?.strictlyAvoid[0];
                return (
                  <tr key={roomType}>
                    <td><Typography level="body-xs" fontWeight="lg">{roomType}</Typography></td>
                    <td>
                      {bestDir ? (
                        <Chip size="sm" color="success" variant="soft" sx={{ fontSize: '0.7rem' }}>
                          {bestDir} ({rec.scores[bestDir]?.toFixed(1)})
                        </Chip>
                      ) : <Typography level="body-xs" sx={{ opacity: 0.5 }}>—</Typography>}
                    </td>
                    <td>
                      {worstDir ? (
                        <Chip size="sm" color="danger" variant="solid" sx={{ fontSize: '0.7rem' }}>
                          {worstDir} ({rec.scores[worstDir]?.toFixed(1)})
                        </Chip>
                      ) : <Typography level="body-xs" sx={{ opacity: 0.5 }}>None</Typography>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Sheet>

      {/* Room Recommendations */}
      <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
        <Typography level="title-sm" sx={{ mb: 2 }}>Personalized Room Recommendations</Typography>
        
        <Table
          size="sm"
          sx={{
            '& th': {
              bgcolor: 'background.level1',
              fontWeight: 600,
              fontSize: '0.75rem',
            },
            '& td': {
              py: 1,
              fontSize: '0.75rem',
            },
          }}
        >
          <thead>
            <tr>
              <th style={{ width: '20%' }}>Room Type</th>
              <th style={{ width: '25%' }}>Most Preferred</th>
              <th style={{ width: '25%' }}>Suitable</th>
              <th style={{ width: '30%' }}>Strictly Avoid</th>
            </tr>
          </thead>
          <tbody>
            {ROOM_TYPES.map((roomType: VastuRoomType) => {
              const rec = roomRecommendations[roomType];
              if (!rec) return null;

              return (
                <tr key={roomType}>
                  <td>
                    <Typography level="body-sm" fontWeight="lg">{roomType}</Typography>
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {rec.preferred.map((dir: VastuDirection) => (
                        <Chip key={dir} size="sm" color="success" variant="soft" sx={{ fontSize: '0.7rem' }}>
                          {dir} ({(rec.scores as any)[dir]?.toFixed(1) || 'N/A'})
                        </Chip>
                      ))}
                    </Stack>
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {rec.suitable.map((dir: VastuDirection) => (
                        <Chip key={dir} size="sm" color="primary" variant="soft" sx={{ fontSize: '0.7rem' }}>
                          {dir} ({(rec.scores as any)[dir]?.toFixed(1) || 'N/A'})
                        </Chip>
                      ))}
                    </Stack>
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {rec.strictlyAvoid.map((dir: VastuDirection) => (
                        <Chip key={dir} size="sm" color="danger" variant="solid" sx={{ fontSize: '0.7rem' }}>
                          {dir} ({(rec.scores as any)[dir]?.toFixed(1) || 'N/A'})
                        </Chip>
                      ))}
                    </Stack>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Sheet>
    </Box>
  );
}
